import {
    isHotspotTab,
    isQuestion,
    isSequencedFlowStep,
    isStartingMessage,
    Item,
    NavigationRestriction,
    RestrictionType,
    Widget,
    WidgetRequirements,
    WidgetsCompletion,
} from '@ael/core-api';
import {makeAutoObservable, runInAction} from 'mobx';

export enum ProgressBarStepType {
  Completed = 'Completed',
  Restricted = 'Restricted',
  Audio = 'Audio',
  InProgress = 'InProgress',
}

export class ContentRequirements {
  private _itemRestrictions: NavigationRestriction | null;
  private _studentFulfilledItemRestrictions: boolean;
  private _widgetCompletion: WidgetsCompletion | null;
  private _onFulfilled: () => void;
  private _wasCompleted: boolean;

  private constructor(
    wasCompleted: boolean,
    itemRestrictions: NavigationRestriction | null,
    widgetCompletion: WidgetsCompletion | null,
    onFulfilled: () => void
  ) {
    this._wasCompleted = wasCompleted;
    makeAutoObservable(this);
    this._itemRestrictions = itemRestrictions;
    this._widgetCompletion = widgetCompletion;
    this._studentFulfilledItemRestrictions = false;
    this._onFulfilled = onFulfilled;
  }

  fulfillItemRequirements = () => {
    runInAction(() => {
      this._studentFulfilledItemRestrictions = true;

      if (this.fulfilled) {
        this._onFulfilled();
      }
    });
  };

  fulfillWidget = (widget: Widget) => {
    runInAction(() => {
      if (!this._widgetCompletion) {
        return;
      }
      this._widgetCompletion = WidgetRequirements.addCompletedWidget(this._widgetCompletion, widget);

      if (this.fulfilled) {
        this._onFulfilled();
      }
    });
  };

  get fulfilled() {
    return this._wasCompleted || (this.itemRestrictionsAreFulfilled && this._widgetRequirementsAreCompleted);
  }

  get _widgetRequirementsAreCompleted() {
    return !this._widgetCompletion || WidgetRequirements.allRequiredWidgetsAreCompleted(this._widgetCompletion);
  }

  get hasNoItemRestrictions() {
    const itemRestrictionsAreNotSet = this._itemRestrictions?.restrictionType === RestrictionType.none;

    return this._itemRestrictions === null || itemRestrictionsAreNotSet;
  }

  get isRestricted() {
    return !this.hasNoItemRestrictions;
  }

  get isRestrictedByListenTime() {
    return this._itemRestrictions?.restrictionType === RestrictionType.audioDuration;
  }

  get time() {
    return this._itemRestrictions?.restrictionType === RestrictionType.customTime ? this._itemRestrictions.duration : 0;
  }

  get progressBarStepType() {
    if (this.fulfilled) {
      return ProgressBarStepType.Completed;
    }
    if (this.isRestrictedByListenTime) {
      return ProgressBarStepType.Audio;
    } else if (this.itemRestrictionsAreFulfilled === true && this._widgetRequirementsAreCompleted === false) {
      return ProgressBarStepType.InProgress;
    } else {
      return ProgressBarStepType.Restricted;
    }
  }

  get itemRestrictionsAreFulfilled() {
    return this.hasNoItemRestrictions || this._studentFulfilledItemRestrictions === true;
  }

  private static _calculateWidgetRequirementForItem = (item: Item): WidgetsCompletion | null => {
    if (isQuestion(item) || isStartingMessage(item) || isHotspotTab(item) || isSequencedFlowStep(item)) {
      return WidgetRequirements.createWidgetsCompletion(item.content);
    }

    return null;
  };

  private static _getItemRestrictions = (item: Item): NavigationRestriction | null => {
    if (isQuestion(item) || isStartingMessage(item) || isHotspotTab(item) || isSequencedFlowStep(item)) {
      return item.restriction;
    }
    return null;
  };

  static createContentRequirements = (wasCompleted: boolean, item: Item, save: () => void) => {
    const itemRestrictions = ContentRequirements._getItemRestrictions(item);
    const widgetCompletion = ContentRequirements._calculateWidgetRequirementForItem(item);
    const contentRequirements = new ContentRequirements(wasCompleted, itemRestrictions, widgetCompletion, save);
    if (!wasCompleted && contentRequirements.fulfilled) {
      save();
    }
    return contentRequirements;
  };
}
